import React from 'react';
import { Wrench, Hammer, HardHat, ArrowLeft, ArrowRight } from 'lucide-react';

interface RenovationLevelStepProps {
  value: {
    renovationLevel: 'light' | 'medium' | 'heavy' | null;
  };
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const RenovationLevelStep: React.FC<RenovationLevelStepProps> = ({
  value,
  onChange,
  onNext,
  onBack
}) => {
  const handleLevelSelect = (level: 'light' | 'medium' | 'heavy') => {
    onChange('renovationLevel', level);
  };

  return (
    <div className="space-y-6">
      {/* Boutons de navigation compacts en haut */}
      <div className="flex justify-between items-center pb-4 border-b border-neutral-200">
        <h2 className="text-base font-semibold text-primary">Niveau de rénovation</h2>
        <div className="flex items-center gap-2 ml-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Précédent
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={!value.renovationLevel}
            className={`px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-1 ${
              !value.renovationLevel ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Suivant
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <LevelCard
          icon={<Wrench className="h-8 w-8" />}
          type="light"
          label="Léger"
          description="Rafraîchissement, peinture, petits travaux"
          selected={value.renovationLevel === 'light'}
          onClick={() => handleLevelSelect('light')}
        />
        <LevelCard
          icon={<Hammer className="h-8 w-8" />}
          type="medium"
          label="Moyen"
          description="Rénovation partielle, mise aux normes"
          selected={value.renovationLevel === 'medium'}
          onClick={() => handleLevelSelect('medium')}
        />
        <LevelCard
          icon={<HardHat className="h-8 w-8" />}
          type="heavy"
          label="Lourd"
          description="Rénovation complète, restructuration"
          selected={value.renovationLevel === 'heavy'}
          onClick={() => handleLevelSelect('heavy')}
        />
      </div>
    </div>
  );
};

interface LevelCardProps {
  icon: React.ReactNode;
  type: string;
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}

const LevelCard: React.FC<LevelCardProps> = ({
  icon,
  label,
  description,
  selected,
  onClick
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      card flex flex-col items-center p-6 transition-all
      ${selected ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-neutral-50'}
    `}
  >
    <div className={`mb-4 ${selected ? 'text-primary' : 'text-neutral-600'}`}>
      {icon}
    </div>
    <span className={`font-medium mb-2 ${selected ? 'text-primary' : 'text-neutral-700'}`}>
      {label}
    </span>
    <p className="text-sm text-neutral-500 text-center">
      {description}
    </p>
  </button>
);

export default RenovationLevelStep;